import { createApp } from 'vue'
import App from '@/App.vue'
import router from '@/router'
import store from './store'
import firebase from 'firebase'
import vuetify from './plugins/vuetify'
import emitter from '@/eventBus'
import helperPlugin from './helpers/helperPlugin'
import VueUploadComponent from 'vue-upload-component'
import Vue3Tour from 'vue3-tour'
import 'vue3-tour/dist/vue3-tour.css'
import VueGtag from 'vue-gtag-next'
import 'vuetify/styles'
import JsonViewer from 'vue3-json-viewer'
import 'vue3-json-viewer/dist/vue3-json-viewer.css'
import './styles/global.scss'
import './styles/highlight.css'
import { createVPhoneInput } from 'v-phone-input'
import 'v-phone-input/dist/v-phone-input.css'
import 'flag-icons/css/flag-icons.min.css'
import Swal from 'sweetalert2'
import { i18n, loadLocaleMessages } from './plugins/i18n'
import { getSavedSiteLocale, setSiteLocale } from '@/helpers/siteLocale'
import { inferDefaultSiteLocale } from '@/helpers/inferDefaultSiteLocale'
import { journeyVerboseLog } from '@/helpers/journeyVerboseLog'
import { currentTheme } from '@/plugins/vuetify'
import { primeLhcApi } from '@/services/LiveHelperChatService'

// Seed LHC_API.args.lhc_base_url before any chat widget script can load so
// its internal error logger always has a valid POST target (prevents noisy
// "Cannot read properties of undefined (reading 'lhc_base_url')" errors).
primeLhcApi()

let app = null
/** Prevents parallel bootstraps when onAuthStateChanged fires more than once before mount (cross-origin redirects). */
let appBootstrapStarted = false

if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (
      event?.message &&
      event.message.includes('ResizeObserver loop completed')
    ) {
      event.stopImmediatePropagation()
      event.preventDefault()
    }
  })
  const existingOnError = window.onerror
  window.onerror = function (message, source, lineno, colno, error) {
    if (
      typeof message === 'string' &&
      message.includes('ResizeObserver loop completed')
    ) {
      return true
    }
    if (typeof existingOnError === 'function') {
      return existingOnError(message, source, lineno, colno, error)
    }
    return false
  }
}

const vPhoneInput = createVPhoneInput({
  countryIconMode: 'svg',
  enableSearchingCountry: true,
})

firebase.auth().onAuthStateChanged(async (user) => {
  // console.log("🔄 Firebase onAuthStateChanged triggered");
  if (user) {
    await user.reload()
    const currentUser = firebase.auth().currentUser

    // console.log("✅ Authenticated user after reload:", {
    //   uid: currentUser.uid,
    //   email: currentUser.email,
    //   phoneNumber: currentUser.phoneNumber,
    // });

    store.commit('UserData/setUser', {
      uid: currentUser.uid,
      email: currentUser.email,
      emailVerified: currentUser.emailVerified,
      phoneNumber: currentUser.phoneNumber,
    })

    store.commit('UserData/setIsAuthenticated', true)
  } else {
    journeyVerboseLog('🚫 No user signed in')
  }

  if (!app && !appBootstrapStarted) {
    appBootstrapStarted = true
    try {
      const instance = createApp(App)
      .use(router)
      .use(store)
      .use(vuetify)
      .use(Vue3Tour)
      .use(vPhoneInput)
      .use(JsonViewer)
      .use(i18n)
      .use(
        VueGtag,
        {
          id: 'G-PPN7NRV6YG',
        },
        router
      )
      .use(helperPlugin)

    // Dev only: `?clearSiteLang=1` simulates first visit (removes localStorage) for locale QA
    if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
      try {
        const sp = new URLSearchParams(window.location.search)
        if (sp.get('clearSiteLang') === '1') {
          localStorage.removeItem('siteLang')
        }
      } catch {
        /* empty */
      }
    }

    // Initialize site locale: saved preference, else infer from location / browser, else en
    const raw = getSavedSiteLocale()
    const hasStored = raw != null && String(raw).trim() !== ''
    const savedLang = hasStored
      ? String(raw).trim().toLowerCase() || 'en'
      : await inferDefaultSiteLocale()
    // Persist preference + <html lang> only; do NOT set vue-i18n locale until messages exist —
    // setting "pt" before setLocaleMessage('pt') yields raw keys on first paint (e.g. Woo → app).
    setSiteLocale(savedLang, { skipI18n: true })

    // Load translations dynamically
    const loadTranslations = async () => {
      try {
        // Always load English first as the base fallback
        const enMessages = await loadLocaleMessages('en')
        i18n.global.setLocaleMessage('en', enMessages)
        journeyVerboseLog(`[i18n] Loaded English translations`)

        // Load the saved language if it's different from English
        if (savedLang !== 'en') {
          const messages = await loadLocaleMessages(savedLang)
          if (!messages || Object.keys(messages).length === 0) {
            console.warn(
              `[i18n] No messages for ${savedLang} — using English; syncing siteLang`
            )
            i18n.global.locale.value = 'en'
            setSiteLocale('en')
          } else {
            i18n.global.setLocaleMessage(savedLang, messages)
            i18n.global.locale.value = savedLang
            journeyVerboseLog(`[i18n] Loaded translations for ${savedLang}`)
          }
        } else {
          i18n.global.locale.value = 'en'
        }

        // Also load Portuguese as it's commonly used
        try {
          const ptMessages = await loadLocaleMessages('pt')
          i18n.global.setLocaleMessage('pt', ptMessages)
          journeyVerboseLog(`[i18n] Loaded Portuguese translations`)
        } catch (ptError) {
          console.warn(
            '[i18n] Could not load Portuguese translations:',
            ptError
          )
        }
      } catch (error) {
        console.error('[i18n] Error loading translations:', error)
        // Fallback to English; keep localStorage in sync (siteLang was set to pt pre-load)
        try {
          const enMessages = await loadLocaleMessages('en')
          i18n.global.setLocaleMessage('en', enMessages)
          i18n.global.locale.value = 'en'
          setSiteLocale('en')
        } catch (fallbackError) {
          console.error(
            '[i18n] Error loading fallback translations:',
            fallbackError
          )
        }
      }
    }

    // Load translations before mounting
    await loadTranslations()
    // Align moment + <html lang> + storage with final vue-i18n locale (loadTranslations may fall back).
    setSiteLocale(i18n.global.locale.value || 'en', { skipI18n: true })

    instance.config.globalProperties.$isDarkTheme = () =>
      currentTheme.value === 'dark'

    instance.config.globalProperties.$swal = Swal
    instance.component('file-upload', VueUploadComponent)
    instance.config.globalProperties.emitter = emitter

    instance.mount('#app')
    app = instance
    } catch (bootstrapErr) {
      appBootstrapStarted = false
      console.error('[bootstrap] Failed to create or mount app:', bootstrapErr)
      throw bootstrapErr
    }
  }
})
